import React, { FC } from 'react';
import { Button } from '../atomic/buttons';
import { DialogBox } from '../atomic/dialog';
import { Table, columnDefiner } from '../atomic/table';
import { Loading } from '../atomic/effects';
import * as DateUtils from '../../utils/DateUtils';
import { removeUser, resendPassword, userApi } from '../../APIs/userApi';
import { Link } from 'react-router';
import { UserModel, UserRole } from '../../models/UserModel';
import { ErrorDisplay } from '../../components/Errors';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useGet } from '../../hooks/useApi';

type Prop = {};
interface UserData extends UserModel {
    trClass: string;
}
const definer = columnDefiner<UserData>();

const columns = [
    definer.accessor('userId', {
        header: 'ID',
        cell: (d) => {
            const { userId } = d.row.original;
            const { userList } = d.table.options;
            return (
                <>
                    <Link
                        to={`/app/admin/user/edit`}
                        state={{
                            user: userList?.find(
                                (item) => item.userId == userId
                            ),
                        }}
                    >
                        <div className="folder name link-area">
                            <span>{d.getValue()}</span>
                        </div>
                    </Link>
                </>
            );
        },
    }),
    {
        header: '名前',
        cell: (d) => {
            const { familyName, givenName } = d.row.original;
            if (!familyName) {
                return '';
            }
            return `${familyName} ${givenName}`;
        },
    },
    definer.accessor('role', {
        header: '権限',
        cell: (d) => UserRole[d.getValue()] ?? 'unknown',
    }),
    definer.accessor('email', {
        header: 'メールアドレス',
        cell: (d) => d.getValue(),
    }),
    definer.accessor('phoneNumber', {
        header: '電話番号',
        cell: (d) => d.getValue(),
    }),
    definer.accessor('lastAccessedAt', {
        header: '最終アクセス日時',
        cell: (d) => (d.getValue() ? DateUtils.reformat(d.getValue()) : '-'),
    }),
    definer.accessor('createdAt', {
        header: '登録日',
        cell: (d) => DateUtils.reformat(d.getValue()),
    }),
    {
        header: '操作',
        cell: (d) => {
            const { userId, email, givenName, familyName, userStatus } =
                d.row.original;
            const {
                onRemoveUser,
                userInfo,
                setResendPassUserId,
                setTargetUser,
            } = d.table.options;
            const target = familyName
                ? `${familyName} ${givenName}さん`
                : email;
            const resentPassword = userStatus === 'FORCE_CHANGE_PASSWORD';
            return (
                <>
                    <Button
                        className="danger"
                        onClick={() => {
                            onRemoveUser(userId, target);
                        }}
                        disabled={userInfo ? userInfo.email == email : true}
                    >
                        ユーザ削除
                    </Button>
                    {resentPassword && (
                        <>
                            <Button
                                className="danger"
                                onClick={() => {
                                    setResendPassUserId(userId);
                                    setTargetUser(target);
                                }}
                            >
                                PASS再送
                            </Button>
                        </>
                    )}
                </>
            );
        },
    },
];

export const UserList: FC<Prop> = () => {
    const [userList, setUserList] = React.useState<Array<UserModel>>([]);
    const [loadUserError, setLoadUserError] = React.useState<string>('');
    const [removeUserId, setRemoveUserId] = React.useState<string>('');
    const [isRemoving, setIsRemoving] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const { user: userInfo } = useAuth();
    const [resendPassUserId, setResendPassUserId] = React.useState<string>('');

    const { isExternalApi } = useTenant();
    const [removeApiKeyUserId, setRemoveApiKeyUserId] =
        React.useState<string>('');
    const [targetUser, setTargetUser] = React.useState<string>('');

    React.useEffect(() => {
        const add = columns.some((item, index) => {
            //console.log(item);
            return item.header === 'APIキー削除';
        });
        if (isExternalApi && add == false) {
            columns.splice(-2, 0, {
                header: 'APIキー削除',
                cell: (d) => {
                    const { userId, role, email, givenName, familyName } =
                        d.row.original;
                    const { onRemoveApiKey } = d.table.options;
                    if (role === 'External' || role === 'SuperAdmin') {
                        return <></>;
                    }
                    const target = familyName
                        ? `${familyName} ${givenName}さん`
                        : email;
                    return (
                        <Button
                            className="danger"
                            onClick={() => {
                                onRemoveApiKey(userId, target);
                            }}
                        >
                            キー削除
                        </Button>
                    );
                },
            });
        }
    }, [isExternalApi]);

    const { data: usersData, error } = useGet(userApi.get.users());

    React.useEffect(() => {
        if (usersData) {
            setUserList(
                usersData.data.sort(
                    (a, b) =>
                        DateUtils.getTime(b.createdAt) -
                        DateUtils.getTime(a.createdAt)
                )
            );
            setIsLoading(false);
        }
        if (error) {
            setLoadUserError('ユーザリストの取得に失敗しました');
            setIsLoading(false);
        }
    }, [usersData, error]);

    const onRemoveApiKey = React.useCallback(
        (userId: string, target: string) => {
            //console.log('remove api key');
            setTargetUser(target);
            setRemoveApiKeyUserId(userId);
        },
        [removeApiKeyUserId]
    );

    const onRemoveApiKeyDialogOK = React.useCallback(
        (dialog: DialogBox) => {
            setIsRemoving(true);
            userApi.delete
                .userApiKey(removeApiKeyUserId)
                .then((resp) => {
                    if (resp.success) {
                        setRemoveApiKeyUserId('');
                    } else {
                        dialog?.addError(resp, 'APIキーの削除に失敗しました');
                    }
                })
                .catch((err) => {
                    //console.warn(err);
                    dialog?.addError(err, 'APIキーの削除に失敗しました');
                })
                .finally(() => {
                    setIsRemoving(false);
                });
        },
        [removeApiKeyUserId]
    );

    const onRemoveApiKeyDialogCancel = () => {
        setRemoveApiKeyUserId('');
    };

    const onRemoveUser = React.useCallback((id: string, target: string) => {
        setTargetUser(target);
        setRemoveUserId(id);
    }, []);

    const onResentPassOk = React.useCallback(
        (dialog: DialogBox) => {
            setIsRemoving(true);
            const user = resendPassword(resendPassUserId)
                .then((resp) => {
                    setResendPassUserId('');
                })
                .catch((err) => {
                    dialog?.addError(err, '再送に失敗しました');
                })
                .finally(() => {
                    setIsRemoving(false);
                });
        },
        [resendPassUserId]
    );

    const onRemoveUserDialogOK = React.useCallback(
        (dialog: DialogBox) => {
            setIsRemoving(true);
            removeUser(removeUserId)
                .then((resp) => {
                    //console.log(resp);
                    if (resp.success && resp.data) {
                        const { userId } = resp.data;
                        setUserList((prevState) => {
                            return prevState?.filter(
                                (item) => item.userId != userId
                            );
                        });
                        setRemoveUserId('');
                    } else {
                        dialog.addError(resp, 'ユーザの削除に失敗しました');
                    }
                })
                .catch((err) => {
                    //console.warn(err);
                    dialog.addError(err, 'ユーザの削除に失敗しました');
                })
                .finally(() => {
                    setIsRemoving(false);
                });
        },
        [removeUserId]
    );

    const onRemoveUserDialogCancel = () => {
        setRemoveUserId('');
    };

    const options = {
        onRemoveUser,
        setResendPassUserId,
        userList,
        userInfo,
        onRemoveApiKey,
        setTargetUser,
    };

    if (isLoading || !userInfo) {
        return <Loading />;
    }
    if (loadUserError) {
        return <ErrorDisplay error={Error(loadUserError)} />;
    }
    return (
        <>
            <div className="edit-forms">
                <Table
                    className="user-table"
                    data={userList}
                    columns={columns}
                    options={options}
                />
            </div>
            {resendPassUserId && (
                <DialogBox
                    title="パスワード再送"
                    onOK={onResentPassOk}
                    onCancel={() => setResendPassUserId('')}
                >
                    {targetUser}にパスワードを再送しても良いですか？
                </DialogBox>
            )}
            {removeUserId != '' && (
                <DialogBox
                    title="ユーザ削除"
                    onOK={onRemoveUserDialogOK}
                    onCancel={onRemoveUserDialogCancel}
                >
                    {targetUser}のアカウントを削除しても良いですか？
                </DialogBox>
            )}
            {isRemoving && <Loading />}

            {removeApiKeyUserId != '' && (
                <DialogBox
                    title="APIキー削除"
                    onOK={onRemoveApiKeyDialogOK}
                    onCancel={onRemoveApiKeyDialogCancel}
                >
                    {targetUser}のAPIキーを削除しても良いですか？
                </DialogBox>
            )}
            {isRemoving && <Loading />}
        </>
    );
};
